import { useState, useEffect, useCallback } from 'react';
import { useStore } from '../store';
import { DocumentConfig, DocumentCategory } from '../types/document';
import {
  CATEGORIES,
  getDocumentTypeById,
  getPageFormatById,
  getDocumentTypesForCategory,
  getPageFormatsForDocumentType,
} from '../data/document-presets';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Check,
  BookOpen,
  Briefcase,
  Scale,
  Sparkles,
  Megaphone,
  Baby,
  BookText,
  Book,
  GraduationCap,
  MessageSquare,
  Layers,
  Image,
  Feather,
  ChefHat,
  Palette,
  FileText,
  FileCheck,
  Users,
  FileSearch,
  Presentation,
  ClipboardList,
  AlertCircle,
  FileSignature,
  Gavel,
  ScrollText,
  Film,
  Clapperboard,
  BookMarked,
  Newspaper,
  LayoutPanelLeft,
  Mail,
  FileImage,
  Grid,
  Share2,
  LucideIcon,
} from 'lucide-react';

// Icon mapping for dynamic icon rendering
const iconMap: Record<string, LucideIcon> = {
  BookOpen,
  Briefcase,
  Scale,
  Sparkles,
  Megaphone,
  Baby,
  BookText,
  Book,
  GraduationCap,
  MessageSquare,
  Layers,
  Image,
  Feather,
  ChefHat,
  Palette,
  FileText,
  FileCheck,
  Users,
  FileSearch,
  Presentation,
  ClipboardList,
  AlertCircle,
  FileSignature,
  Gavel,
  Handshake: FileCheck, // Fallback for Handshake
  ScrollText,
  Film,
  Theater: Clapperboard, // Fallback for Theater
  BookMarked,
  Newspaper,
  LayoutPanelLeft,
  Mail,
  FileImage,
  Grid,
  Share2,
  PresentationIcon: Presentation,
};

interface ProjectWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (config: DocumentConfig) => void;
}

type WizardStep = 1 | 2 | 3 | 4;

export default function ProjectWizard({ isOpen, onClose, onComplete }: ProjectWizardProps) {
  const { user } = useStore();

  const [step, setStep] = useState<WizardStep>(1);
  const [selectedCategory, setSelectedCategory] = useState<DocumentCategory | null>(null);
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState(user?.name || '');
  const [description, setDescription] = useState('');

  // Reset wizard when opened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedCategory(null);
      setSelectedDocType(null);
      setSelectedFormat(null);
      setTitle('');
      setAuthor(user?.name || '');
      setDescription('');
    }
  }, [isOpen, user?.name]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleCategorySelect = useCallback((categoryId: DocumentCategory) => {
    setSelectedCategory(categoryId);
    setSelectedDocType(null);
    setSelectedFormat(null);
    setStep(2);
  }, []);

  const handleDocTypeSelect = useCallback((docTypeId: string) => {
    setSelectedDocType(docTypeId);
    const docType = getDocumentTypeById(docTypeId);
    if (docType) {
      setSelectedFormat(docType.defaultFormat);
    }
    setStep(3);
  }, []);

  const handleFormatSelect = useCallback((formatId: string) => {
    setSelectedFormat(formatId);
    setStep(4);
  }, []);

  const handleComplete = useCallback(() => {
    if (!selectedCategory || !selectedDocType || !selectedFormat) return;

    const config: DocumentConfig = {
      category: selectedCategory,
      documentType: selectedDocType,
      pageFormat: selectedFormat,
      metadata: {
        title: title || 'Untitled Document',
        author: author || 'Anonymous',
        description: description || undefined,
      },
    };

    onComplete(config);
  }, [selectedCategory, selectedDocType, selectedFormat, title, author, description, onComplete]);

  const goBack = useCallback(() => {
    if (step > 1) {
      setStep((step - 1) as WizardStep);
    }
  }, [step]);

  const getIcon = (iconName: string) => {
    const IconComponent = iconMap[iconName];
    return IconComponent || BookOpen;
  };

  if (!isOpen) return null;

  const availableDocTypes = selectedCategory ? getDocumentTypesForCategory(selectedCategory) : [];
  const availableFormats = selectedDocType ? getPageFormatsForDocumentType(selectedDocType) : [];
  const selectedDocTypeInfo = selectedDocType ? getDocumentTypeById(selectedDocType) : null;
  const selectedFormatInfo = selectedFormat ? getPageFormatById(selectedFormat) : null;
  const selectedCategoryInfo = selectedCategory ? CATEGORIES.find(c => c.id === selectedCategory) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">New Project</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {step === 1 && 'Choose a document category'}
              {step === 2 && 'Select your document type'}
              {step === 3 && 'Choose page format'}
              {step === 4 && 'Review and start'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    s === step
                      ? 'bg-accent text-white'
                      : s < step
                      ? 'bg-accent/20 text-accent'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {s < step ? <Check size={16} /> : s}
                </div>
                {s < 4 && (
                  <div
                    className={`w-12 h-0.5 mx-1 transition-all ${
                      s < step ? 'bg-accent' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center gap-8 mt-2 text-xs text-gray-500">
            <span className={step === 1 ? 'text-accent font-medium' : ''}>Category</span>
            <span className={step === 2 ? 'text-accent font-medium' : ''}>Type</span>
            <span className={step === 3 ? 'text-accent font-medium' : ''}>Format</span>
            <span className={step === 4 ? 'text-accent font-medium' : ''}>Details</span>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Category Selection */}
          {step === 1 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {CATEGORIES.map((category) => {
                const IconComponent = getIcon(category.icon);
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategorySelect(category.id)}
                    className="group p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-accent hover:shadow-lg transition-all text-left"
                    style={{ '--category-color': category.color } as React.CSSProperties}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors"
                      style={{ backgroundColor: `${category.color}20`, color: category.color }}
                    >
                      <IconComponent size={24} />
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-accent transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-sm text-gray-500">{category.description}</p>
                  </button>
                );
              })}
            </div>
          )}

          {/* Step 2: Document Type Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <button
                onClick={goBack}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-accent transition-colors mb-4"
              >
                <ChevronLeft size={16} />
                Back to categories
              </button>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {availableDocTypes.map((docType) => {
                  const IconComponent = getIcon(docType.icon);
                  return (
                    <button
                      key={docType.id}
                      onClick={() => handleDocTypeSelect(docType.id)}
                      className={`group p-4 bg-white border-2 rounded-xl hover:border-accent hover:shadow-md transition-all text-left ${
                        selectedDocType === docType.id ? 'border-accent bg-accent/5' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 group-hover:bg-accent/10 transition-colors">
                          <IconComponent size={20} className="text-gray-600 group-hover:text-accent transition-colors" />
                        </div>
                        <div className="min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm group-hover:text-accent transition-colors">
                            {docType.name}
                          </h4>
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                            {docType.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Page Format Selection */}
          {step === 3 && (
            <div className="space-y-4">
              <button
                onClick={goBack}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-accent transition-colors mb-4"
              >
                <ChevronLeft size={16} />
                Back to document types
              </button>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {availableFormats.map((format) => {
                  const aspectRatio = format.widthInches / format.heightInches;
                  const isDefault = selectedDocTypeInfo?.defaultFormat === format.id;

                  // Calculate preview dimensions (max 100px height)
                  const previewHeight = 80;
                  const previewWidth = previewHeight * aspectRatio;

                  return (
                    <button
                      key={format.id}
                      onClick={() => handleFormatSelect(format.id)}
                      className={`group p-4 bg-white border-2 rounded-xl hover:border-accent hover:shadow-md transition-all ${
                        selectedFormat === format.id ? 'border-accent bg-accent/5' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        {/* Page Preview */}
                        <div
                          className="bg-white border-2 border-gray-300 rounded shadow-sm mb-3 flex items-center justify-center group-hover:border-accent transition-colors"
                          style={{
                            width: `${previewWidth}px`,
                            height: `${previewHeight}px`,
                          }}
                        >
                          <span className="text-[10px] text-gray-400">{format.shortName}</span>
                        </div>

                        <h4 className="font-medium text-gray-900 text-sm text-center group-hover:text-accent transition-colors">
                          {format.name}
                        </h4>

                        <p className="text-xs text-gray-500 mt-1">
                          {format.widthInches}" x {format.heightInches}"
                        </p>

                        {isDefault && (
                          <span className="mt-2 px-2 py-0.5 bg-accent/10 text-accent text-xs rounded-full">
                            Recommended
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 4: Confirm & Start */}
          {step === 4 && (
            <div className="space-y-6">
              <button
                onClick={goBack}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-accent transition-colors"
              >
                <ChevronLeft size={16} />
                Back to format selection
              </button>

              {/* Summary */}
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <h3 className="font-medium text-gray-900 mb-3">Project Configuration</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Category</span>
                    <p className="font-medium text-gray-900 mt-0.5">{selectedCategoryInfo?.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Document Type</span>
                    <p className="font-medium text-gray-900 mt-0.5">{selectedDocTypeInfo?.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Page Format</span>
                    <p className="font-medium text-gray-900 mt-0.5">
                      {selectedFormatInfo?.name} ({selectedFormatInfo?.shortName})
                    </p>
                  </div>
                </div>
              </div>

              {/* Document Details Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter your document title"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Author
                  </label>
                  <input
                    type="text"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Enter author name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of your document"
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent outline-none transition-all resize-none"
                  />
                </div>
              </div>

              {/* Features Preview */}
              {selectedDocTypeInfo && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Document Features</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedDocTypeInfo.features.showImages && (
                      <span className="px-2 py-1 bg-white text-xs text-blue-700 rounded border border-blue-200">
                        Images
                      </span>
                    )}
                    {selectedDocTypeInfo.features.showCover && (
                      <span className="px-2 py-1 bg-white text-xs text-blue-700 rounded border border-blue-200">
                        Cover Page
                      </span>
                    )}
                    {selectedDocTypeInfo.features.showChapters && (
                      <span className="px-2 py-1 bg-white text-xs text-blue-700 rounded border border-blue-200">
                        Chapters
                      </span>
                    )}
                    {selectedDocTypeInfo.features.showPageNumbers && (
                      <span className="px-2 py-1 bg-white text-xs text-blue-700 rounded border border-blue-200">
                        Page Numbers
                      </span>
                    )}
                    {selectedDocTypeInfo.features.showHeaders && (
                      <span className="px-2 py-1 bg-white text-xs text-blue-700 rounded border border-blue-200">
                        Headers
                      </span>
                    )}
                    {selectedDocTypeInfo.features.showDataViz && (
                      <span className="px-2 py-1 bg-white text-xs text-blue-700 rounded border border-blue-200">
                        Charts & Tables
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>

          {step === 4 && (
            <button
              onClick={handleComplete}
              disabled={!title.trim()}
              className="flex items-center gap-2 px-6 py-2.5 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              Start Project
              <ChevronRight size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
